import { useContext, useEffect, useMemo } from 'react'
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  limit,
  orderBy,
  query,
  updateDoc,
  writeBatch,
} from 'firebase/firestore'
import { useCollection } from 'react-firebase-hooks/firestore'
import { firebase_firestore } from '../common/firebaseConfig'
import { SnackbarHelperContext } from '../contexts/SnackbarHelperContext'

export type BacktestFolder = {
  id: string
  name: string
  backtestIds: string[]
  order: number
  createdAt: number
}

type FolderReassignmentOp = {
  folderId: string
  op: 'add' | 'remove'
}

// Returns only the folders that actually need to change: the target folder
// gaining the id (unless targetFolderId is null) and any other folder that
// currently holds the id losing it. Callers apply these as atomic
// arrayUnion/arrayRemove operations rather than replacing the whole array,
// so a stale local `folders` snapshot can't clobber a concurrent write.
export function computeFolderReassignment(
  folders: BacktestFolder[],
  backtestId: string,
  targetFolderId: string | null
): FolderReassignmentOp[] {
  const changes: FolderReassignmentOp[] = []

  folders.forEach((folder) => {
    const hasBacktest = folder.backtestIds?.includes(backtestId) ?? false

    if (folder.id === targetFolderId) {
      if (!hasBacktest) {
        changes.push({ folderId: folder.id, op: 'add' })
      }
    } else if (hasBacktest) {
      changes.push({ folderId: folder.id, op: 'remove' })
    }
  })

  return changes
}

function useBacktestFolders(gcid?: string | null, enabled = true) {
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const foldersQuery = useMemo(() => {
    if (!gcid || !enabled) return null
    return query(
      collection(firebase_firestore, `usermeta/${gcid}/savedBacktestFolders`),
      orderBy('order'),
      limit(500)
    )
  }, [gcid, enabled])

  const [snapshot, loading, foldersError] = useCollection(foldersQuery)

  const folders: BacktestFolder[] = useMemo(() => {
    if (!snapshot) return []
    return snapshot.docs.map((doc) => ({
      ...(doc.data() as Omit<BacktestFolder, 'id'>),
      id: doc.id,
    }))
  }, [snapshot])

  function reportError(message: string, error: unknown) {
    console.error(message, error)
    setSnackbarSettings({
      severity: 'error',
      message,
      autoHideDuration: 10000,
    })
  }

  useEffect(() => {
    if (foldersError) {
      reportError('Failed to load folders', foldersError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foldersError])

  async function createFolder(name: string) {
    if (!gcid) return
    try {
      const foldersRef = collection(firebase_firestore, `usermeta/${gcid}/savedBacktestFolders`)
      await addDoc(foldersRef, {
        name,
        backtestIds: [],
        order: Date.now(),
        createdAt: Date.now(),
      })
    } catch (error) {
      reportError('Failed to create folder', error)
    }
  }

  async function renameFolder(folderId: string, name: string) {
    if (!gcid) return
    try {
      await updateDoc(
        doc(firebase_firestore, `usermeta/${gcid}/savedBacktestFolders/${folderId}`),
        {
          name,
        }
      )
    } catch (error) {
      reportError('Failed to rename folder', error)
    }
  }

  async function deleteFolder(folderId: string) {
    if (!gcid) return
    try {
      await deleteDoc(doc(firebase_firestore, `usermeta/${gcid}/savedBacktestFolders/${folderId}`))
    } catch (error) {
      reportError('Failed to delete folder', error)
    }
  }

  async function moveBacktestToFolder(backtestId: string, targetFolderId: string | null) {
    if (!gcid) return
    const changes = computeFolderReassignment(folders, backtestId, targetFolderId)
    if (changes.length === 0) return

    try {
      const batch = writeBatch(firebase_firestore)
      changes.forEach((change) => {
        const folderRef = doc(
          firebase_firestore,
          `usermeta/${gcid}/savedBacktestFolders/${change.folderId}`
        )
        batch.update(folderRef, {
          backtestIds: change.op === 'add' ? arrayUnion(backtestId) : arrayRemove(backtestId),
        })
      })
      await batch.commit()
    } catch (error) {
      reportError('Failed to move backtest to folder', error)
    }
  }

  return { folders, loading, createFolder, renameFolder, deleteFolder, moveBacktestToFolder }
}

export default useBacktestFolders
